import React from 'react'
import { Button, Flex, H2, InputModal } from '../../lib/components'
import { Pagination } from '../../lib/components/Pagination'
import { getFormattedDate } from '../../lib/utils/helpers'
import {
  OpportunityNewsDraft,
  OpportunityNewsItem,
  OpportunityNewsPagination,
} from '../../services/opportunity-news-service'
import './opportunity-news-list.scss'
import './opportunity-news-modal.scss'

export type NewsModal = 'edit' | 'delete' | null

type OpportunityNewsListPresenterProps = {
  items: OpportunityNewsItem[]
  pagination: OpportunityNewsPagination
  currentPage: number
  isLoading: boolean
  modal: NewsModal
  selected: OpportunityNewsItem | null
  draft: OpportunityNewsDraft
  draftError: string
  isSaving: boolean
  isDeleting: boolean
  onDraftChange: (field: keyof OpportunityNewsDraft, value: string) => void
  onSpotlightChange: (value: boolean) => void
  onEdit: (row: OpportunityNewsItem) => void
  onDelete: (row: OpportunityNewsItem) => void
  onCloseModal: () => void
  onSave: () => Promise<void>
  onConfirmDelete: () => Promise<void>
  handleCurrentPage: (page: number) => void
}

function OpportunityNewsListPresenter({
  items,
  pagination,
  currentPage,
  isLoading,
  modal,
  selected,
  draft,
  draftError,
  isSaving,
  isDeleting,
  onDraftChange,
  onSpotlightChange,
  onEdit,
  onDelete,
  onCloseModal,
  onSave,
  onConfirmDelete,
  handleCurrentPage,
}: OpportunityNewsListPresenterProps) {
  return (
    <Flex variant='column-center'>
      <div className='news-list w-100'>
        <div className='news-list__header'>
          <H2 text='News' />
        </div>

        {isLoading ? (
          <p className='news-list__message'>Loading…</p>
        ) : items.length === 0 ? (
          <p className='news-list__message'>No news found</p>
        ) : (
          <>
            <div className='news-list__table-wrap'>
              <table className='news-list__table'>
                <colgroup>
                  <col style={{ width: '140px' }} />
                  <col style={{ width: '200px' }} />
                  <col />
                  <col style={{ width: '90px' }} />
                  <col style={{ width: '110px' }} />
                  <col style={{ width: '200px' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th scope='col'>Released</th>
                    <th scope='col'>Opportunity</th>
                    <th scope='col'>Headline</th>
                    <th scope='col'>Link</th>
                    <th scope='col'>Spotlight</th>
                    <th scope='col' className='news-list__th--actions'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id}>
                      <td>{getFormattedDate(new Date(row.releaseDate), 'DD MMM YYYY')}</td>
                      <td>{row.opportunityTitle ?? row.opportunityDomain}</td>
                      <td>{row.headline}</td>
                      <td>
                        {row.link ? (
                          <a
                            className='news-list__link'
                            href={row.link}
                            target='_blank'
                            rel='noreferrer'
                          >
                            Link
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{row.spotlight ? 'Yes' : 'No'}</td>
                      <td>
                        <div className='news-list__actions'>
                          <Button
                            text='Edit'
                            variant='secondary-btn'
                            handleClick={() => onEdit(row)}
                            classes={['news-list__action-btn']}
                            dataTestId={`news-edit-${row.id}`}
                          />
                          <Button
                            text='Delete'
                            variant='secondary-btn'
                            handleClick={() => onDelete(row)}
                            classes={['news-list__action-btn']}
                            dataTestId={`news-delete-${row.id}`}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentPage}
              handleCurrentPage={handleCurrentPage}
              pagination={pagination}
            />
          </>
        )}
      </div>

      {modal === 'edit' && selected && (
        <InputModal onBackdropClick={isSaving ? undefined : onCloseModal}>
          <div className='input-modal news-modal' data-testid='news-edit-modal'>
            <h3 className='news-modal__title'>Edit news</h3>

            <label className='news-modal__label' htmlFor='news-headline'>
              Headline <span className='news-modal__required'>*</span>
            </label>
            <input
              id='news-headline'
              className='news-modal__input'
              value={draft.headline}
              maxLength={500}
              onChange={(e) => onDraftChange('headline', e.target.value)}
            />

            <label className='news-modal__label' htmlFor='news-description'>
              Description
            </label>
            <textarea
              id='news-description'
              className='news-modal__input news-modal__textarea'
              value={draft.description}
              maxLength={2000}
              onChange={(e) => onDraftChange('description', e.target.value)}
            />

            <label className='news-modal__label' htmlFor='news-link'>
              Link
            </label>
            <input
              id='news-link'
              className='news-modal__input'
              placeholder='https://'
              value={draft.link}
              maxLength={2048}
              onChange={(e) => onDraftChange('link', e.target.value)}
            />

            <label className='news-modal__label' htmlFor='news-thumbnail'>
              Thumbnail link
            </label>
            <input
              id='news-thumbnail'
              className='news-modal__input'
              placeholder='https://'
              value={draft.thumbnailUrl}
              maxLength={2048}
              onChange={(e) => onDraftChange('thumbnailUrl', e.target.value)}
            />

            <label className='news-modal__label' htmlFor='news-spotlight'>
              <input
                id='news-spotlight'
                type='checkbox'
                checked={draft.spotlight}
                onChange={(e) => onSpotlightChange(e.target.checked)}
              />{' '}
              Spotlight this news
            </label>

            {draftError && <span className='news-modal__error'>{draftError}</span>}

            <div className='news-modal__actions'>
              <Button
                text='Cancel'
                variant='secondary-btn'
                handleClick={onCloseModal}
                isDisabled={isSaving}
                classes={['news-list__action-btn']}
                dataTestId='news-edit-cancel'
              />
              <Button
                text={isSaving ? 'Saving…' : 'Save'}
                variant='primary-btn'
                handleClick={onSave}
                isDisabled={isSaving}
                classes={['news-list__action-btn']}
                dataTestId='news-edit-save'
              />
            </div>
          </div>
        </InputModal>
      )}

      {modal === 'delete' && selected && (
        <InputModal onBackdropClick={isDeleting ? undefined : onCloseModal}>
          <div className='input-modal news-modal' data-testid='news-delete-modal'>
            <h3 className='news-modal__title'>Delete this news?</h3>
            <p className='news-modal__text'>&ldquo;{selected.headline}&rdquo; will be removed.</p>

            <div className='news-modal__actions'>
              <Button
                text='No'
                variant='secondary-btn'
                handleClick={onCloseModal}
                isDisabled={isDeleting}
                classes={['news-list__action-btn']}
                dataTestId='news-delete-no'
              />
              <Button
                text={isDeleting ? 'Deleting…' : 'Yes'}
                variant='primary-btn'
                handleClick={onConfirmDelete}
                isDisabled={isDeleting}
                classes={['news-list__action-btn']}
                dataTestId='news-delete-yes'
              />
            </div>
          </div>
        </InputModal>
      )}
    </Flex>
  )
}

export { OpportunityNewsListPresenter }
